import moment from "moment";
import { formatPlanAsText } from "./aiPrompt";
import { translate } from "../i18n/messages";

// Exports of the current plan: Markdown to paste into an invitation or a
// protocol, and an .ics calendar entry for the whole session.

function planEnd(starttime, agenda) {
  return agenda.reduce(
    (end, card) => end.add(moment.duration(card.time)),
    starttime.clone()
  );
}

// German readers expect 20.10.2026, everyone else gets ISO 2026-10-20.
const DATE_FORMAT = { en: "YYYY-MM-DD", de: "DD.MM.YYYY" };

export function buildMarkdown(starttime, agenda, link, language = "en") {
  const t = (key) => translate(language, key);
  const end = planEnd(starttime, agenda);
  return `**${t("exports.agenda")}** · ${starttime.format(
    DATE_FORMAT[language] || DATE_FORMAT.en
  )}, ${starttime.format("HH:mm")}–${end.format("HH:mm")}

${formatPlanAsText(starttime, agenda, language)}

${t("exports.liveTimer")}: ${link}`;
}

// RFC 5545 text: escape backslash, semicolon, comma and newlines.
function icsText(text) {
  return text
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\r?\n/g, "\\n");
}

// Lines longer than 75 octets are folded: CRLF plus a leading space.
// Counted in UTF-8 bytes, without splitting a multi-byte character.
function utf8Length(char) {
  const code = char.codePointAt(0);
  if (code < 0x80) return 1;
  if (code < 0x800) return 2;
  return code < 0x10000 ? 3 : 4;
}

function foldLine(line) {
  const parts = [];
  let current = "";
  let size = 0;
  for (const char of line) {
    const charSize = utf8Length(char);
    const limit = parts.length ? 74 : 75;
    if (size + charSize > limit) {
      parts.push(current);
      current = "";
      size = 0;
    }
    current += char;
    size += charSize;
  }
  parts.push(current);
  return parts.join("\r\n ");
}

const icsDate = (time) => time.clone().utc().format("YYYYMMDD[T]HHmmss[Z]");

export function buildIcs(
  starttime,
  agenda,
  link,
  { language = "en", title = translate(language, "exports.agenda"), now = moment() } = {}
) {
  const end = planEnd(starttime, agenda);
  const description = `${formatPlanAsText(
    starttime,
    agenda,
    language
  )}\n\n${translate(language, "exports.liveTimer")}: ${link}`;
  return [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//focussuite.de//Agenda//EN",
    "CALSCALE:GREGORIAN",
    "BEGIN:VEVENT",
    `UID:${starttime.valueOf()}-${agenda.length}@focussuite.de`,
    `DTSTAMP:${icsDate(now)}`,
    `DTSTART:${icsDate(starttime)}`,
    `DTEND:${icsDate(end)}`,
    `SUMMARY:${icsText(title)}`,
    `DESCRIPTION:${icsText(description)}`,
    `URL:${link}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ]
    .map(foldLine)
    .join("\r\n")
    .concat("\r\n");
}

// Hands a generated file to the browser as a download.
export function downloadFile(filename, content, type) {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}
